import * as Location from "expo-location";
import * as TaskManager from "expo-task-manager";
import { AppState, Platform } from "react-native";

import { API_BASE_URL } from "@/api/config";
import { createBackgroundAttemptMemory } from "@/lib/background-attempt-memory";
import type { BackgroundLocationResult } from "@/lib/stay-visible-plans";
import {
  countWake,
  parseDiagnostics,
  startDiagnostics,
  type StayVisibleDiagnostics,
  msSinceLastOk,
  type WakeOutcome,
} from "@/lib/stay-visible-diagnostics";
import { fixAgeMs } from "@/lib/fix-age";
import { classifyFix } from "@/lib/presence-anchor";
import {
  readPresenceAnchor,
  recordPublishedAnchor,
  writePresenceAnchor,
} from "@/lib/presence-anchor-io";
import { batchAges, newestFix } from "@/lib/newest-fix";
import {
  backgroundPermissionRoute,
  type BackgroundRoutePreviewResult,
} from "@/lib/background-permission-route";
import { openAppSettings } from "@/lib/location-access";
import i18n from "@/i18n";
import { storage } from "@/lib/storage";
import { probe } from "@/lib/timing-probe";
import { withTimeout } from "@/lib/with-timeout";

// Re-exported so callers keep one import site for the whole feature.
export type { BackgroundRoutePreviewResult };

const TASK_NAME = "opncue-stay-visible";

/**
 * Whether a wake whose fix shows no meaningful movement sends ONLY the
 * ultralight liveness signal (`POST /presence/liveness`) instead of the
 * publish pair.
 *
 * **false until the step-10 server eligibility flip.** Today the server shows
 * a person only while their `last_seen` is fresh (`presenceTtl`, 300 s), and
 * only the publish pair refreshes it — a liveness-only quiet wake against
 * TODAY's server would make a stationary background user vanish from Nearby
 * in five minutes while the app tells them they are visible. The release
 * order is recorded in the roadmap (step 11): server flips eligibility to
 * lease + liveness + anchor first, THEN this flips true in the build shipped
 * with it. Until then the movement gate still runs on every wake — its
 * classification is probed (`stayvisible.gate.*`) so the device pass can
 * judge the math against a real pocket — but a quiet verdict changes nothing
 * the server sees.
 */
export const QUIET_WAKE_LIVENESS_ONLY = false;
const SESSION_KEY = "opncue.session";

/**
 * How long to wait for the app to leave the foreground after asking Android for
 * background location, and how long to then wait for it to come back. If it
 * never leaves, no settings page opened and the request's own answer stands.
 */
const SETTINGS_OPEN_GRACE_MS = 1_500;
const SETTINGS_RETURN_TIMEOUT_MS = 120_000;


export type { BackgroundLocationResult };

/** Where the background task leaves a note about how its last ping went. */
const PING_KEY = "opncue.stayVisible.ping";

/**
 * How many pings in a row may fail before the app treats staying-visible as
 * broken. One is a tunnel; three in a row is three minutes of the user being
 * told they are visible while nobody can see them.
 */
export const STAY_VISIBLE_FAILURE_LIMIT = 3;

export type StayVisiblePing = {
  /** ISO time of the last ping the server accepted. */
  okAt: string | null;
  /** Consecutive failures since that success. */
  failures: number;
  /** What went wrong last, or null when the last ping worked. */
  reason: "network" | "unauthorized" | "no_accuracy" | null;
};

const NO_PINGS: StayVisiblePing = { okAt: null, failures: 0, reason: null };

/**
 * The background task's report card.
 *
 * It exists because the task cannot raise anything itself: it runs with no UI,
 * often with the app not even in memory. Every failure used to end in an empty
 * `.catch(() => {})`, so a user could be un-pingable for hours while the app,
 * the health check and the Android notification all said they were visible.
 * Now the task writes down what happened and `verify()` reads it on resume.
 */
export async function readStayVisiblePing(): Promise<StayVisiblePing> {
  try {
    const raw = await storage.get(PING_KEY);
    if (!raw) return NO_PINGS;
    return { ...NO_PINGS, ...(JSON.parse(raw) as Partial<StayVisiblePing>) };
  } catch {
    return NO_PINGS;
  }
}

async function recordPing(reason: StayVisiblePing["reason"]): Promise<void> {
  try {
    const previous = await readStayVisiblePing();
    const next: StayVisiblePing = reason
      ? {
          okAt: previous.okAt,
          failures: previous.failures + 1,
          reason,
        }
      : { okAt: new Date().toISOString(), failures: 0, reason: null };
    await storage.set(PING_KEY, JSON.stringify(next));
  } catch {
    /* the note is best effort — never break the task over it */
  }
}

const DIAGNOSTICS_KEY = "opncue.stayVisible.diagnostics";

/**
 * Read the wake counters. Separate from the ping record ON PURPOSE — nothing
 * acts on these, so counting them can never demote or hide anybody. See
 * `stay-visible-diagnostics.ts`.
 */
export async function readStayVisibleDiagnostics(): Promise<StayVisibleDiagnostics> {
  return parseDiagnostics(await storage.get(DIAGNOSTICS_KEY).catch(() => null));
}

/**
 * Record one wake and how it ended. Called on EVERY exit path of the task.
 *
 * Best effort and silent: a diagnostic that can break the thing it measures is
 * worse than no diagnostic. `timing-probe.ts` makes the same rule its second
 * property.
 */
async function countTaskWake(outcome: WakeOutcome): Promise<void> {
  try {
    const previous = await readStayVisibleDiagnostics();
    const at = new Date().toISOString();
    // Read BEFORE the update, so this is the gap the wake actually closed
    // rather than zero. `sanitize` drops a null, so the first wake of a window
    // simply omits the field instead of reporting a misleading 0.
    const sinceOkMs = msSinceLastOk(previous, at);
    const next = countWake(previous, outcome, at);
    await storage.set(DIAGNOSTICS_KEY, JSON.stringify(next));

    // SCAFFOLDING — remove with the rest of the `stayvisible.` probes once the
    // wake cadence is known. The counters are readable in Profile, but that
    // needs somebody holding the phone; this makes the same facts fall out of
    // `adb logcat | grep opncue-probe` while the device sits untouched, which
    // is the entire point of measuring a STATIONARY phone.
    //
    // The outcome rides in the NAME because `probe` carries only numbers —
    // `timing-probe.ts` sets that rule and gives this exact shape for anything
    // categorical.
    probe(`stayvisible.wake.${outcome}`, {
      wakes: next.wakes,
      ok: next.ok,
      failed: next.pingFailed,
      // The gap the soak is actually judged on: a run of wakes proves the task
      // is being called, and only this proves presence never expired between
      // two of them. Compare against the server's presence TTL.
      ...(sinceOkMs === null ? {} : { sinceOkMs }),
    });
  } catch {
    /* never break the task over a counter */
  }
}

/** Begin a fresh counting window, when the task is (re)started. */
async function restartDiagnostics(): Promise<void> {
  try {
    const fresh = startDiagnostics(new Date().toISOString());
    await storage.set(DIAGNOSTICS_KEY, JSON.stringify(fresh));
  } catch {
    /* ignore */
  }
}

/** Forget the failure history (e.g. after the user repairs or stops). */
export async function clearStayVisiblePings(): Promise<void> {
  try {
    await storage.remove(PING_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Mint a fresh access token from the stored refresh token, and save it.
 *
 * The background task cannot use the API client's refresh: that lives on
 * module state belonging to a running app, and this task is frequently woken
 * into a process where no app has started. It re-implements the one call.
 *
 * Safe to do from here only because `/auth/refresh` does NOT rotate — the same
 * refresh token comes back, deliberately (see `auth.service.ts`). A rotating
 * endpoint would mean a background refresh could invalidate the token the
 * foreground app is holding and sign the user out from their pocket.
 */
async function refreshFromStorage(
  session: Record<string, unknown> & { refreshToken: string },
): Promise<string | null> {
  const res = await fetch(`${API_BASE_URL}/auth/refresh`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refreshToken: session.refreshToken }),
  });
  if (!res.ok) return null;
  const next = (await res.json()) as { accessToken?: string };
  if (!next.accessToken) return null;
  await storage.set(SESSION_KEY, JSON.stringify({ ...session, ...next }));
  return next.accessToken;
}

/** The four ways staying-visible can break. */
export type LocationBreak =
  | "services_off" // device Location Services are off
  | "foreground_revoked" // "While Using" permission gone
  | "background_revoked" // "Always" permission gone
  | "background_unavailable"; // can't run background updates / task stopped

export type LocationHealth =
  { ok: true } | { ok: false; reason: LocationBreak };

/**
 * Background keep-alive: while the user has chosen to stay visible, this task
 * receives periodic location updates (even when the phone is locked) and pings
 * the backend so their presence stays fresh at the venue. Runs only in a
 * native dev/production build — Expo Go can't execute background tasks.
 *
 * Privacy: each ping re-confirms the user is still near their current venue
 * while they've chosen to be visible. No history of where they have been is kept.
 */
// Registered at module load on native so the OS can wake it in the background.
// Skipped on web where TaskManager isn't available.
if (Platform.OS !== "web") {
  TaskManager.defineTask(TASK_NAME, async ({ data, error }) => {
    // Every exit below records exactly once, which is the whole point: `wakes`
    // then equals the number of times the OS actually called us, and "iOS never
    // woke the task" stops being indistinguishable from "iOS woke it and it
    // achieved nothing". These three exits recorded NOTHING before.
    if (error) {
      await countTaskWake("error");
      return;
    }
    const locations = (data as { locations?: Location.LocationObject[] })
      ?.locations;
    // NEWEST, not `[0]`. Core Location delivers a deferred batch oldest-first,
    // so `[0]` was the stalest fix in it — see `newest-fix.ts` for why the
    // choice is by timestamp rather than by position, and why single-fix
    // batches hid this for so long.
    const loc = newestFix(locations);
    if (!loc) {
      await countTaskWake("no_fix");
      return;
    }

    // SCAFFOLDING — out with the rest of the `stayvisible.` probes. Batching is
    // the one thing we still cannot observe: 828 wakes on device produced zero
    // failures, and the server accepts a fix up to fifteen minutes old, so
    // nothing so far shows whether a multi-fix batch has EVER arrived. Without
    // this, a build carrying the fix above is indistinguishable from one
    // without it. `selected` should equal `newest` on every delivery.
    const ages = batchAges(locations, Date.now());
    if (ages && ages.count > 1) {
      probe("stayvisible.batch", {
        n: ages.count,
        oldest: ages.oldestAgeMs,
        newest: ages.newestAgeMs,
        selected: fixAgeMs(loc.timestamp),
      });
    }

    const { latitude, longitude, accuracy } = loc.coords;

    // A fix with no stated accuracy is worse than no fix at all. The server
    // stores whatever we send and then refuses to apply ANY radius rule to a
    // coordinate whose uncertainty is unknown — so checking in without `acc`
    // does not merely fail to help, it overwrites a good foreground fix with an
    // unusable one and drops the user out of Nearby in both directions. Better
    // to send nothing and let presence lapse on its own `presenceTtl` clock,
    // which is at least honest about not knowing where they are. The foreground
    // heartbeat makes the same choice for the same reason — see
    // `makePresencePing`.
    if (accuracy === null || accuracy === undefined) {
      await recordPing("no_accuracy");
      await countTaskWake("no_accuracy");
      return;
    }

    try {
      const raw = await storage.get(SESSION_KEY);
      if (!raw) {
        // Nothing stored means signed out — not a fault worth reporting to the
        // USER, and there is nobody to report it to. Still counted, because a
        // wake is a wake and this is where "the OS is calling us but nothing
        // happens" would otherwise hide.
        await countTaskWake("signed_out");
        return;
      }
      const session = JSON.parse(raw) as {
        accessToken?: string;
        refreshToken?: string;
        userId?: string;
      };
      if (!session.accessToken || !session.userId) {
        // No token OR no identity is the same fact for this task: nobody to
        // act as. The anchor machinery below is owner-gated, so without a
        // userId there is no anchor to read and none may be written.
        await countTaskWake("signed_out");
        return;
      }

      // The movement gate (roadmap step 11). Runs on EVERY wake — including
      // while the quiet path below is still switched off — so the
      // classification is observable on hardware before it decides anything.
      const decision = classifyFix(await readPresenceAnchor(session.userId), {
        lat: latitude,
        lng: longitude,
        accuracyM: accuracy,
      });

      // SCAFFOLDING — out with the rest of the `stayvisible.` probes. The
      // device pass judges the gate on these: a still phone should converge
      // to `quiet` with a stable `widened`, and a walk across the venue
      // should flip it to `moved`. Categorical reason rides in the name,
      // numbers in the payload — `timing-probe.ts` sets that shape.
      probe(
        `stayvisible.gate.${decision.kind === "quiet" ? "quiet" : decision.reason}`,
        {
          d: Math.round(decision.distanceM),
          acc: Math.round(accuracy),
          ...(decision.kind === "quiet"
            ? { widened: Math.round(decision.anchor.uncertaintyM) }
            : {}),
        },
      );

      if (decision.kind === "quiet" && QUIET_WAKE_LIVENESS_ONLY) {
        // No meaningful movement: the server needs to know the device is
        // alive, and nothing else. One TTL'd Redis SET server-side; no
        // position in the request at all, so this wake cannot move anybody.
        // Bounded like the API client's requests (15 s, its measured choice):
        // a hung fetch would otherwise consume the wake WITHOUT reaching
        // recordPing/countTaskWake, and an unobserved wake is the exact
        // failure shape the ledgers exist to make visible. `withTimeout`
        // cannot abort the underlying request — it settles our control flow
        // and lets the orphan die with the process, which for a headless
        // task is the trade that keeps the accounting honest. Timing out is
        // NOT a 401, so it never burns the refresh retry.
        const signalLiveness = async (token: string): Promise<number> => {
          const res = await withTimeout(
            fetch(`${API_BASE_URL}/presence/liveness`, {
              method: "POST",
              headers: { Authorization: `Bearer ${token}` },
            }),
            15_000,
          );
          if (res === null) return -1;
          return res.ok ? 0 : res.status;
        };

        let status = await signalLiveness(session.accessToken);
        if (status === 401 && session.refreshToken) {
          const refreshed = await refreshFromStorage({
            ...session,
            refreshToken: session.refreshToken,
          });
          if (refreshed) status = await signalLiveness(refreshed);
        }

        if (status === 0) {
          // The quiet wake's one obligation to the cache: the fix proved the
          // person is within d + accuracy of the anchor, so the stored
          // uncertainty widens to say so. Movement is never laundered into
          // false precision — and when widening passes ANCHOR_UNUSABLE_M,
          // classifyFix stops returning "quiet" and the next wake publishes.
          await writePresenceAnchor(decision.anchor);
          await recordPing(null);
          await countTaskWake("ok");
          return;
        }
        await recordPing(status === 401 ? "unauthorized" : "network");
        await countTaskWake("ping_failed");
        return;
      }

      /**
       * One round of pings. Returns the first status that was not a success,
       * or 0 when both calls landed.
       *
       * `GET /venues/current` is the call that does the work: it saves the fix,
       * resolves the space and checks the user in. `POST /presence/join` then
       * keeps them visible there.
       */
      const ping = async (token: string): Promise<number> => {
        const auth = { Authorization: `Bearer ${token}` };
        const query = new URLSearchParams({
          lat: String(latitude),
          lng: String(longitude),
          acc: String(accuracy),
          // Same reason as the foreground client. An OS-delivered background
          // fix is usually current, but the task can be woken with a batched or
          // deferred update, and a retry after a token refresh sends the SAME
          // `loc` some seconds later — so the age is read from the fix rather
          // than assumed to be zero.
          age: String(fixAgeMs(loc.timestamp)),
        });
        const venue = await fetch(
          `${API_BASE_URL}/venues/current?${query.toString()}`,
          { headers: auth },
        );
        if (!venue.ok) return venue.status;
        const join = await fetch(`${API_BASE_URL}/presence/join`, {
          method: "POST",
          headers: { ...auth, "Content-Type": "application/json" },
        });
        return join.ok ? 0 : join.status;
      };

      let status = await ping(session.accessToken);

      // Access tokens last 15 minutes and this task runs for hours. Without a
      // refresh here every ping after the first quarter-hour was a 401 into a
      // swallowed catch, which is exactly how someone stays "visible" all
      // evening without appearing to anyone.
      if (status === 401 && session.refreshToken) {
        const refreshed = await refreshFromStorage({
          ...session,
          refreshToken: session.refreshToken,
        });
        if (refreshed) status = await ping(refreshed);
      }

      if (status === 0) {
        // This wake PUBLISHED, so the cache mirrors what the server now
        // holds: this fix, at its reported accuracy. (A null-accuracy fix
        // cannot reach here — the guard above returns before the session is
        // even read.)
        await recordPublishedAnchor({
          ownerId: session.userId,
          lat: latitude,
          lng: longitude,
          accuracyM: accuracy,
        });
      }
      await recordPing(
        status === 0 ? null : status === 401 ? "unauthorized" : "network",
      );
      await countTaskWake(status === 0 ? "ok" : "ping_failed");
    } catch {
      // Never throw from a background task — but do leave a mark, so the app
      // can tell the user their visibility has stopped working.
      await recordPing("network");
      await countTaskWake("ping_failed");
    }
  });
}

/**
 * Waits out a trip to the system settings page, resolving true once the app has
 * left the foreground and come back. Resolves false when it never left, which
 * means no settings page opened.
 */
function awaitSettingsRoundTrip(): Promise<boolean> {
  return new Promise((resolve) => {
    // Subscribing happens after the request resolved, by which point the
    // settings page may already be up — so read the current state rather than
    // waiting for a "change" event that has been and gone.
    let left = AppState.currentState !== "active";
    let settled = false;

    const finish = (madeTrip: boolean) => {
      if (settled) return;
      settled = true;
      clearTimeout(graceTimer);
      clearTimeout(returnTimer);
      subscription.remove();
      resolve(madeTrip);
    };

    const subscription = AppState.addEventListener("change", (next) => {
      if (next !== "active") {
        left = true;
        return;
      }
      if (left) finish(true);
    });

    const graceTimer = setTimeout(() => {
      if (!left) finish(false);
    }, SETTINGS_OPEN_GRACE_MS);

    // They went to settings and never came back to us. Give up rather than
    // hold the promise (and the prompt's spinner) open forever.
    const returnTimer = setTimeout(() => finish(left), SETTINGS_RETURN_TIMEOUT_MS);
  });
}

/**
 * Ask for background ("Always" / "Allow all the time") location.
 *
 * Four outcomes, and the distinction between the last two is the whole point:
 * "granted" and "denied" are answers; "unavailable" means the platform cannot
 * ever do this (web), so persisting the app-open-only fallback is correct; and
 * "unknown" means we failed to find out — a timed-out read, a native throw,
 * Settings that would not open. Expo Go now reports "unknown" rather than
 * "unavailable", because from here it is indistinguishable from a wedged
 * module on a real device. See `stay-visible-plans.ts` for what each may do.
 *
 * Foreground has to be granted first — every platform refuses background
 * without it — which onboarding has normally already done by this point.
 */
/** Set before the request, so a termination mid-prompt cannot lose the fact. */
/**
 * Whether this phone has already been shown the prompt.
 *
 * Backed by the keychain AND by a session flag, in its own dependency-free
 * module so that its failure behaviour can be driven by a real test. Both
 * halves matter: `storage.set` rejects on a keychain error and `storage.get`
 * returns `null` on one, which is indistinguishable from "never asked". Either
 * would drop the user back into the silent-refusal bug.
 */
const attemptMemory = createBackgroundAttemptMemory(storage);

/**
 * How long the permission READS may take. Not the prompts — see below.
 *
 * Bounding the keychain alone would have been a half-fix that read like a whole
 * one: these two OS reads sit in the same `Promise.all` as the keychain read,
 * so either of them wedging leaves the sheet's button disabled and reading
 * "Requesting…" exactly as a wedged keychain did. A deadline on one of three
 * siblings bounds nothing.
 */
const PERMISSION_READ_BUDGET_MS = 2000;

/**
 * Everything the route decision needs, read from the OS and from storage.
 *
 * `null` means we could not find out in time. Callers turn that into
 * "unknown" — NOT "unavailable", which is a claim about the platform and is
 * consumed by persisting a permanent fallback. Guessing a route is equally
 * wrong: sending someone to Settings on a timed-out read, or firing a prompt
 * iOS will silently swallow, are both worse than saying we do not know.
 */
async function readRouteInputs(): Promise<
  Parameters<typeof backgroundPermissionRoute>[0] | null
> {
  const reads = await withTimeout(
    Promise.all([
      Location.getForegroundPermissionsAsync(),
      Location.getBackgroundPermissionsAsync(),
      attemptMemory.wasAttempted(),
    ]),
    PERMISSION_READ_BUDGET_MS,
  );
  if (!reads) return null;

  const [foreground, background, attemptedBefore] = reads;
  return {
    platform: Platform.OS === "android" ? "android" : Platform.OS === "web" ? "web" : "ios",
    backgroundStatus: background.status,
    canAskAgain: background.canAskAgain,
    attemptedBefore,
    freshPermissionCycle: foreground.status === "undetermined",
  };
}

/**
 * Where a request would go, for the UI to label its button before the tap.
 *
 * **Always resolves to a real route, and the non-null return type is the
 * guarantee.** The caller treats `null` as "still working it out" and disables
 * the button while it waits — so a `null` here was not a cautious answer, it
 * was a permanently dead button reading "Requesting…". That is reachable: the
 * OS reads below throw in Expo Go and unsupported environments, which is
 * exactly what `requestBackgroundLocation` catches to return "unknown". The
 * graceful handling for that case already existed in the modal and could never
 * be reached, because the button that leads to it could not be pressed.
 *
 * "unknown" is the honest answer to a read we could not make: it labels the
 * button neutrally, promises no Settings trip, and lets the tap through to
 * `requestBackgroundLocation`, which resolves the same way and explains itself.
 * NOT "unavailable" — that is a claim about the platform, and the prompt sheet
 * consumes it by persisting a permanent fallback.
 *
 * The tri-state still exists — the caller starts at `null` before this
 * resolves — because a storage read sits in this path and painting a default
 * would flash the wrong label, then change it under the user's thumb.
 */
export async function backgroundRoutePreview(): Promise<BackgroundRoutePreviewResult> {
  if (Platform.OS === "web") return "unavailable";
  try {
    const inputs = await readRouteInputs();
    // Same distinction as the request path: a timed-out or throwing read is
    // "we could not work it out", not "this platform cannot do it".
    return inputs ? backgroundPermissionRoute(inputs) : "unknown";
  } catch {
    return "unknown";
  }
}

/**
 * Wait out a trip to the settings page, then read the answer that trip produced.
 *
 * Shared by both routes that end up there — Android 11+, which is sent by the
 * OS, and iOS, which we send ourselves — because the half that matters is the
 * same for both: the status from before the trip says nothing, and only a fresh
 * read afterwards does.
 */
async function settingsRoundTripResult(
  opened: boolean,
): Promise<BackgroundLocationResult> {
  // Settings would not open. Nobody was asked anything, so this is not a
  // refusal — it used to be recorded as one, which both told the user a
  // falsehood and suppressed the re-prompt backoff.
  if (!opened) return "unknown";

  // We never saw them leave and come back. They may well have granted it; we
  // simply cannot say. Also not a refusal.
  if (!(await awaitSettingsRoundTrip())) return "unknown";

  // Bounded, and this is the read the whole review chain was about: it sits on
  // the button path, so an unbounded one leaves the sheet spinning with nothing
  // to press. A timeout here means we could not check — NOT that they said no.
  const after = await withTimeout(
    Location.getBackgroundPermissionsAsync(),
    PERMISSION_READ_BUDGET_MS,
  );
  if (!after) return "unknown";

  // A completed trip that changed nothing IS an answer, and the only "denied"
  // this function may produce.
  return after.status === "granted" ? "granted" : "denied";
}

export async function requestBackgroundLocation(): Promise<BackgroundLocationResult> {
  if (Platform.OS === "web") return "unavailable";
  try {
    /**
     * Captured BEFORE the foreground request, and that ordering is not
     * cosmetic.
     *
     * `freshPermissionCycle` is how a reinstall is told apart from somebody who
     * has already been asked — see `backgroundPermissionRoute`. Requesting
     * foreground permission first turns `undetermined` into `granted`, so
     * reading it afterwards would report "not a fresh cycle" on every clean
     * install, and a keychain flag surviving from a previous install would then
     * send that user straight to Settings without ever offering the prompt.
     * Exactly the bug the fresh-cycle rule exists to prevent, reintroduced by
     * reading one line too late.
     */
    const foregroundBefore = await withTimeout(
      Location.getForegroundPermissionsAsync(),
      PERMISSION_READ_BUDGET_MS,
    );
    // Timed out reading our own permission state. Not a refusal, and not an
    // unsupported platform — we could not find out.
    if (!foregroundBefore) return "unknown";
    const freshPermissionCycle = foregroundBefore.status === "undetermined";

    if (foregroundBefore.status !== "granted") {
      const requested = await Location.requestForegroundPermissionsAsync();
      if (requested.status !== "granted") return "denied";
    }

    /**
     * The prompts BELOW are deliberately left unbounded, and that is the line
     * this budget draws: a read is a call the OS answers on its own and must
     * never hang the UI, but `requestForegroundPermissionsAsync` and
     * `requestBackgroundPermissionsAsync` are waiting for a human to decide.
     * Putting a deadline on those would abandon a prompt the user is still
     * looking at and record an answer they never gave.
     */
    const inputs = await readRouteInputs();
    if (!inputs) return "unknown";
    const route = backgroundPermissionRoute({ ...inputs, freshPermissionCycle });

    if (route === "granted") return "granted";
    // Unreachable on a device: `backgroundPermissionRoute` returns
    // "unavailable" only for web, and web returned at the top of this function.
    // Kept so the dispatch below covers the union, and documented rather than
    // dressed up as a path that carries native traffic.
    if (route === "unavailable") return "unavailable";
    if (route === "settings") {
      return settingsRoundTripResult(await openAppSettings());
    }

    /**
     * Recorded BEFORE the request, and awaited.
     *
     * If the app is terminated while the OS prompt is on screen — which is
     * precisely when somebody is most likely to leave it — a flag written
     * afterwards is never written at all. The next launch would read "never
     * attempted", ask again, and fall into the silent failure this whole
     * mechanism exists to route around. This is the one write that has to
     * survive the process not surviving.
     *
     * No `.catch()` here, and that is deliberate: `record` cannot reject, and
     * holds the attempt in memory before it touches the keychain, so a failing
     * keychain costs nothing within this process. Guarding the call here would
     * put the failure handling somewhere no test can reach it.
     */
    await attemptMemory.record();

    const bg = await Location.requestBackgroundPermissionsAsync();
    if (bg.status === "granted") return "granted";

    // A prompt was shown and declined. That is a real answer, and NOT grounds
    // for marching somebody into Settings on the same tap — the next deliberate
    // attempt routes there, by which point they have changed their mind.
    if (Platform.OS !== "android") return "denied";

    // Android 11+ has no inline dialog for this one: the call opens the app's
    // settings page and resolves straight away with the answer from *before*
    // the user got there, so the status above says nothing. Waiting for them to
    // come back and reading it again is the only way to know.
    return settingsRoundTripResult(true);
  } catch {
    // A native call threw. Expo Go is the usual cause, but a wedged or missing
    // module on a real device throws the same way — and we cannot tell those
    // apart from here, so this reports "could not find out" rather than
    // claiming the platform cannot do it. Persisting a fallback on a
    // transient throw is the defect this whole change exists to remove.
    return "unknown";
  }
}

/**
 * Health check for staying visible. Runs on app resume and before starting a
 * session. `requireBackground` should be true only when the user opted into
 * background ("enabled") — otherwise a missing Always permission isn't a break.
 */
export async function evaluateLocationHealth(
  requireBackground: boolean,
): Promise<LocationHealth> {
  if (Platform.OS === "web") return { ok: true };
  try {
    if (!(await Location.hasServicesEnabledAsync())) {
      return { ok: false, reason: "services_off" };
    }
    const fg = await Location.getForegroundPermissionsAsync();
    if (fg.status !== "granted") {
      return { ok: false, reason: "foreground_revoked" };
    }
    if (requireBackground) {
      const bg = await Location.getBackgroundPermissionsAsync();
      if (bg.status !== "granted") {
        return { ok: false, reason: "background_revoked" };
      }
    }
    return { ok: true };
  } catch {
    // Can't determine background here (e.g. Expo Go) — only a break if required.
    return requireBackground
      ? { ok: false, reason: "background_unavailable" }
      : { ok: true };
  }
}

/** Whether the background stay-visible task is currently running. */
export async function isStayVisibleRunning(): Promise<boolean> {
  if (Platform.OS === "web") return false;
  try {
    return await Location.hasStartedLocationUpdatesAsync(TASK_NAME);
  } catch {
    return false;
  }
}

/** True only if background location is currently granted. */
export async function hasBackgroundLocation(): Promise<boolean> {
  if (Platform.OS === "web") return false;
  try {
    const bg = await Location.getBackgroundPermissionsAsync();
    return bg.status === "granted";
  } catch {
    return false;
  }
}

/** Begin (or ensure) the background stay-visible session. No-op if unsupported. */
export async function startStayVisible(): Promise<void> {
  if (Platform.OS === "web") return;
  try {
    if (!(await hasBackgroundLocation())) return;
    const running = await Location.hasStartedLocationUpdatesAsync(TASK_NAME);
    if (running) return;
    // A new session starts with a clean record: an old failure streak must not
    // be held against pings that have not happened yet.
    await clearStayVisiblePings();
    // A fresh counting window too, so "14 wakes" is always 14 wakes SINCE this
    // session began rather than a total carried over from an earlier one.
    await restartDiagnostics();
    await Location.startLocationUpdatesAsync(TASK_NAME, {
      accuracy: Location.Accuracy.Balanced,
      // ANDROID-ONLY, and the docs are explicit about it:
      // https://docs.expo.dev/versions/v56.0.0/sdk/location/ marks
      // `timeInterval` "Android only". This used to read "ping about once a
      // minute" with no platform caveat, which made a 60s cadence against a
      // then-120s TTL look like a designed margin. On iOS the option is inert:
      // delivery there is driven by `distanceInterval` and accuracy, i.e. by
      // MOVEMENT, so the cadence is whatever walking around produces — and a
      // stationary phone, whose owner is the most reliably present user there
      // is, produces the least of it.
      //
      // Two things carry iOS instead, and neither is this line:
      // `pausesUpdatesAutomatically: false` below (stops the OS suspending
      // updates for a still device), and `presenceTtl`, widened so that a
      // movement-driven feed has room to be irregular.
      timeInterval: 60_000,
      distanceInterval: 0,
      pausesUpdatesAutomatically: false,
      showsBackgroundLocationIndicator: false,
      // Android-only, and required there: a foreground service must post an
      // ongoing notification, and from API 34 a *location* one must be visible
      // too. There is no way to hide, silence or demote it — so it stays
      // SUBTLE instead. Title only, and deliberately NO notificationColor:
      // passing a color makes expo-location call setColorized(true), which
      // paints the whole notification a solid brand color and reads as the
      // app shouting about tracking. Omitting the body is fine too — the
      // native side treats it as optional (`body?.let`) even though the TS
      // type requires it, hence the cast. Ignored on iOS.
      foregroundService: {
        notificationTitle: i18n.t("visibility.stayVisibleNotificationTitle"),
      } as Location.LocationTaskServiceOptions,
    });
  } catch {
    // Expo Go or missing native config — silently skip.
  }
}

/** Stop the background stay-visible session (e.g. on going invisible/leaving). */
export async function stopStayVisible(): Promise<void> {
  if (Platform.OS === "web") return;
  try {
    if (await Location.hasStartedLocationUpdatesAsync(TASK_NAME)) {
      await Location.stopLocationUpdatesAsync(TASK_NAME);
    }
    await clearStayVisiblePings();
  } catch {
    // ignore
  }
}
